/**
 * RAMS wizard — per-step Zod schemas.
 *
 * Lifted verbatim from `apps/mobile/screens/wizards/WizardRamsScreen.tsx`
 * (Phase 13). Field names, min/max bounds, error messages, and enum
 * values match the source exactly so the mobile UX is unchanged after
 * the refactor.
 *
 * The wizard step order (mirrored in the `RAMS_WIZARD_STEPS` array
 * below) is:
 *
 *   1. context   — project context (job description, address, dates)
 *   2. hazards   — multi-select with severity
 *   3. controls  — per-hazard control entries
 *   4. ppe       — checklist
 *   5. method    — long-form method statement
 *   6. emergency — emergency procedures + assembly point
 *   7. signoff   — free-text sign-off list
 *   8. preview   — read-only summary (no validation)
 *
 * Other web/mobile wizards live alongside in `./` and follow the same
 * shape — see `./index.ts` for the cross-wizard `WIZARD_SCHEMAS` map.
 */
import { z } from "zod";

// ─── Shared sub-schemas ────────────────────────────────────────────────────

export const RamsHazardSeveritySchema = z.enum(["low", "medium", "high"]);
export type RamsHazardSeverity = z.infer<typeof RamsHazardSeveritySchema>;

export const RamsHazardEntrySchema = z.object({
  id: z.string(),
  label: z.string().min(1),
  severity: RamsHazardSeveritySchema,
  notes: z.string().optional(),
});
export type RamsHazardEntry = z.infer<typeof RamsHazardEntrySchema>;

export const RamsControlMeasureSchema = z.object({
  hazardId: z.string(),
  description: z.string().min(5, "Describe the control measure"),
});
export type RamsControlMeasure = z.infer<typeof RamsControlMeasureSchema>;

// ─── Per-step schemas ──────────────────────────────────────────────────────

export const RamsContextStepSchema = z.object({
  jobDescription: z
    .string()
    .min(10, "Describe the job in at least 10 characters"),
  siteAddress: z.string().min(3, "Site address is required"),
  startDate: z.string().optional().default(""),
  endDate: z.string().optional().default(""),
});
export type RamsContextStepData = z.infer<typeof RamsContextStepSchema>;

export const RamsHazardsStepSchema = z.object({
  hazards: z.array(RamsHazardEntrySchema).min(1, "Select at least one hazard"),
});
export type RamsHazardsStepData = z.infer<typeof RamsHazardsStepSchema>;

export const RamsControlsStepSchema = z.object({
  controls: z
    .array(RamsControlMeasureSchema)
    .min(1, "Add at least one control measure"),
});
export type RamsControlsStepData = z.infer<typeof RamsControlsStepSchema>;

export const RamsPpeStepSchema = z.object({
  ppe: z.array(z.string()).min(1, "Pick at least one PPE item"),
});
export type RamsPpeStepData = z.infer<typeof RamsPpeStepSchema>;

export const RamsMethodStepSchema = z.object({
  methodStatement: z
    .string()
    .min(20, "Method statement should be at least 20 characters"),
});
export type RamsMethodStepData = z.infer<typeof RamsMethodStepSchema>;

export const RamsEmergencyStepSchema = z.object({
  emergencyProcedures: z
    .string()
    .min(10, "Describe the emergency procedures"),
  assemblyPoint: z.string().min(3, "Assembly point is required"),
});
export type RamsEmergencyStepData = z.infer<typeof RamsEmergencyStepSchema>;

export const RamsSignOffStepSchema = z.object({
  signOffList: z
    .string()
    .min(2, "List who needs to sign (workers, subs)"),
});
export type RamsSignOffStepData = z.infer<typeof RamsSignOffStepSchema>;

/** Preview is read-only — no validation. */
export const RamsPreviewStepSchema = z.object({});
export type RamsPreviewStepData = z.infer<typeof RamsPreviewStepSchema>;

// ─── Step ordering + composite ─────────────────────────────────────────────

export const RAMS_WIZARD_STEPS = [
  { id: "context", schema: RamsContextStepSchema },
  { id: "hazards", schema: RamsHazardsStepSchema },
  { id: "controls", schema: RamsControlsStepSchema },
  { id: "ppe", schema: RamsPpeStepSchema },
  { id: "method", schema: RamsMethodStepSchema },
  { id: "emergency", schema: RamsEmergencyStepSchema },
  { id: "signoff", schema: RamsSignOffStepSchema },
  { id: "preview", schema: RamsPreviewStepSchema },
] as const;

/**
 * Composite schema for the full RAMS payload. Intersects every
 * non-preview step. Useful for end-of-wizard "validate everything"
 * passes and as a `z.infer<>` source for the mobile `RamsFormData`
 * interface.
 */
export const RamsWizardFullSchema = z.object({
  ...RamsContextStepSchema.shape,
  ...RamsHazardsStepSchema.shape,
  ...RamsControlsStepSchema.shape,
  ...RamsPpeStepSchema.shape,
  ...RamsMethodStepSchema.shape,
  ...RamsEmergencyStepSchema.shape,
  ...RamsSignOffStepSchema.shape,
});
export type RamsWizardData = z.infer<typeof RamsWizardFullSchema>;
